import {Trans} from '@lingui/macro';
import {NodeProvidedProps} from '@visx/network/lib/types';
import React, {FC} from 'react';
import styled from 'styled-components';

import {
  buildHoverParentClassName,
  hoverSelector,
} from '../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {RuleBundleModel} from '../../../../models/api/ruleBundleModel';
import {BUNDLE_NODE_HEIGHT, InboxGraphBundlesNode, NODE_WIDTH} from '../inboxGraphTypes';
import {getNodeHeight} from '../inboxGraphUtils';

/*
 * Style.
 */

const StyledBundlesUl = styled.ul`
  border-radius: 12px;
  background-color: ${(p) => p.theme.greys.shade10};
  border: 2px ${(p) => p.theme.greys.shade50} solid;
  overflow: hidden;
  text-align: left;
  list-style-type: none;
  padding-left: 0;
  margin: 0;

  > li:not(:first-child) {
    border-top: 1px ${(p) => p.theme.greys.shade50} solid;
  }

  > li:hover {
    background-color: ${(p) => p.theme.greys.shade30};
  }
`;

const StyledBundleLi = styled.li`
  cursor: pointer;
  height: ${BUNDLE_NODE_HEIGHT}px;
`;

const StyledBundleNameWrapper = styled.div`
  flex: 1 1 0;
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.medium};
  ${ellipsis()};
`;

const StyledMoreWrapper = styled.div`
  flex: 1 1 0;
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.bold};
`;

const StyledMoreLi = styled.li`
  cursor: pointer;
  height: ${BUNDLE_NODE_HEIGHT}px;
  line-height: ${BUNDLE_NODE_HEIGHT}px;
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade70};
  padding-left: 8px;
`;

const StyledTooltipContent = styled.div`
  font-size: ${fontSizes.small};
`;

const StyledTooltipList = styled.ul`
  padding-left: 0;
  list-style-type: none;
  margin: 4px 10px;
  line-height: 1.7;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  position: static;
  height: 100%;
  width: 100%;

  display: flex;
  flex-direction: row;
  align-items: center;

  > svg {
    flex: 0 0 30px;
  }

  .overlay {
  }

  &:hover .overlay {
  }
`;

const StyledIconOnHover = styled(Icon)`
  flex: 0 0 10px;
  display: none;
  ${hoverSelector} {
    flex: 0 0 30px;
    display: block;
  }
`;

function getMoreBundlesList(moreBundles: ReadonlyArray<RuleBundleModel>) {
  return (
    <StyledTooltipContent>
      <StyledTooltipList>
        {moreBundles.slice(0, 20).map((bundle) => (
          <li key={bundle.id}>{bundle.name}</li>
        ))}
        {moreBundles.length > 20 && (
          <li>
            <Trans id="inboxtraffic-nmore">+ {moreBundles.length - 20} more</Trans>
          </li>
        )}
      </StyledTooltipList>
    </StyledTooltipContent>
  );
}

/*
 * Components.
 */

/**
 * Returns a node representing one or several rule bundles in an inbox graph.
 */
export const BundlesNodeComponent: FC<NodeProvidedProps<InboxGraphBundlesNode>> = ({node}) => {
  const nodeHeight = getNodeHeight(node);
  const theme = useUserTheme();
  const {bundles} = node;
  if (!bundles) {
    return null;
  }

  const displayedBundles = bundles.slice(0, 3);
  const moreBundles = bundles.slice(3);

  return (
    <foreignObject height={nodeHeight + 4} width={NODE_WIDTH} x={NODE_WIDTH / -2} y={nodeHeight / -2}>
      <StyledBundlesUl>
        {displayedBundles.map((bundle) => (
          <BundleNodeComponent {...bundle} onClick={node.onClick} key={bundle.id} />
        ))}
        {/* If there is only one more bundle, let's display it instead of having a +1 more cell. */}
        {moreBundles.length === 1 && <BundleNodeComponent {...moreBundles[0]} onClick={node.onClick} />}
        {/* Otherwise, let's stick with 3 displayed names and a +N more cell. */}
        {moreBundles.length > 1 && (
          <StyledMoreLi onClick={() => node.onClick(undefined)}>
            <StyledTooltipCoordinator
              render={() => getMoreBundlesList(moreBundles)}
              className={buildHoverParentClassName()}
            >
              <StyledMoreWrapper>{`+ ${moreBundles.length} more`}</StyledMoreWrapper>
              <StyledIconOnHover
                name="externalLinkSmall"
                size={14}
                color={theme.greys.shade70}
                className="overlay"
              />
            </StyledTooltipCoordinator>
          </StyledMoreLi>
        )}
      </StyledBundlesUl>
    </foreignObject>
  );
};

const BundleNodeComponent: FC<RuleBundleModel & {onClick: (id: RuleBundleModel | undefined) => void}> = ({
  onClick,
  ...bundle
}) => {
  const theme = useUserTheme();

  return (
    <StyledBundleLi onClick={() => onClick(bundle)}>
      <StyledTooltipCoordinator
        render={() => <StyledTooltipContent>{bundle.name}</StyledTooltipContent>}
        className={buildHoverParentClassName()}
      >
        <Icon name="gearFilledSmall" size={14} color={theme.greys.shade70} />
        <StyledBundleNameWrapper>{bundle.name}</StyledBundleNameWrapper>
        <StyledIconOnHover
          name="externalLinkSmall"
          size={14}
          color={theme.greys.shade70}
          className="overlay"
        />
      </StyledTooltipCoordinator>
    </StyledBundleLi>
  );
};
